import { createFileRoute, Link } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { useTranslation } from "react-i18next";
import { CreditCard, Wallet, Sparkles } from "lucide-react";
import { useBillingStatus } from "@/lib/queries";

export const Route = createFileRoute("/_authenticated/add")({
  head: () => ({
    meta: [
      { title: "Add — VISIKT" },
      { name: "description", content: "Add a subscription or log a salary entry." },
    ],
  }),
  component: AddPage,
});

function AddPage() {
  const { t } = useTranslation();
  const billing = useBillingStatus();
  const showUpgrade = billing.data ? !billing.data.isActive : true;

  return (
    <AppShell>
      <section className="animate-entry">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
          {t("add.eyebrow")}
        </p>
        <h1 className="text-2xl font-display font-bold tracking-tight mb-8">{t("add.title")}</h1>

        <div className="space-y-3">
          <Link
            to="/subscriptions"
            search={{ new: 1 } as never}
            className="block w-full p-5 rounded-2xl border border-foreground bg-foreground text-background"
          >
            <div className="flex items-start gap-4">
              <CreditCard className="size-6 shrink-0 mt-0.5" strokeWidth={1.5} />
              <div className="min-w-0">
                <div className="text-base font-semibold">{t("add.subOption")}</div>
                <div className="text-[12px] opacity-70 mt-1 leading-relaxed">
                  {t("add.subBody")}
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/salary"
            search={{ new: 1 } as never}
            className="block w-full p-5 rounded-2xl border border-border bg-card hover:border-foreground/30 transition-colors"
          >
            <div className="flex items-start gap-4">
              <Wallet className="size-6 shrink-0 mt-0.5 text-muted-foreground" strokeWidth={1.5} />
              <div className="min-w-0">
                <div className="text-base font-semibold">{t("add.incomeOption")}</div>
                <div className="text-[12px] text-muted-foreground mt-1 leading-relaxed">
                  {t("add.incomeBody")}
                </div>
              </div>
            </div>
          </Link>

          {showUpgrade && (
            <Link
              to="/plans"
              className="block w-full p-5 rounded-2xl border border-border bg-card hover:border-foreground/30 transition-colors"
            >
              <div className="flex items-start gap-4">
                <Sparkles
                  className="size-6 shrink-0 mt-0.5 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <div className="min-w-0">
                  <div className="text-base font-semibold">{t("addExtra.upgradeOption")}</div>
                  <div className="text-[12px] text-muted-foreground mt-1 leading-relaxed">
                    {t("addExtra.upgradeBody")}
                  </div>
                </div>
              </div>
            </Link>
          )}
        </div>
      </section>
    </AppShell>
  );
}
